// AreYouSureDialog.tsx
import React from "react";
import ReactDOM from "react-dom";

interface AreYouSureDialogProps {
  message: string;
  onClose: (accept: boolean) => void;
}

interface AreYouSureDialogState {
  mounted: boolean;
  dark: boolean;
  hoverContinue: boolean;
  hoverCancel: boolean;
  activeContinue: boolean;
  activeCancel: boolean;
  focusContinue: boolean;
  focusCancel: boolean;
}

export default class AreYouSureDialog extends React.Component<
  AreYouSureDialogProps,
  AreYouSureDialogState
> {
  private continueRef = React.createRef<HTMLButtonElement>();
  private cancelRef = React.createRef<HTMLButtonElement>();
  private prevOverflow = "";

  constructor(props: AreYouSureDialogProps) {
    super(props);
    this.state = {
      mounted: false,
      dark: false,
      hoverContinue: false,
      hoverCancel: false,
      activeContinue: false,
      activeCancel: false,
      focusContinue: false,
      focusCancel: false,
    };
  }

  private handleKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      this.props.onClose(false);
      return;
    }
    if (e.key === "Enter") {
      this.props.onClose(true);
      return;
    }
    if (e.key === "Tab") {
      const foci = [this.cancelRef.current, this.continueRef.current].filter(Boolean) as HTMLElement[];
      if (foci.length < 2) return;
      const active = document.activeElement as HTMLElement | null;
      const idx = foci.findIndex((el) => el === active);
      if (idx !== -1) {
        e.preventDefault();
        const next = e.shiftKey ? (idx - 1 + foci.length) % foci.length : (idx + 1) % foci.length;
        foci[next].focus();
      }
    }
  };

  componentDidMount() {
    requestAnimationFrame(() => this.setState({ mounted: true }));
    this.continueRef.current?.focus();

    this.prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    this.setState({ dark: !!mq?.matches });
    mq?.addEventListener?.("change", (e) => this.setState({ dark: e.matches }));

    document.addEventListener("keydown", this.handleKey);
  }

  componentWillUnmount() {
    document.body.style.overflow = this.prevOverflow;
    document.removeEventListener("keydown", this.handleKey);
  }

  private handleOverlayClick = () => this.props.onClose(false);
  private stop = (e: React.MouseEvent) => e.stopPropagation();

  render() {
    if (typeof document === "undefined") return null;

    const accent = "#c79656";
    const isDark = this.state.dark;

    // Centered overlay
    const overlayStyle: React.CSSProperties = {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      background: isDark ? "rgba(0,0,0,.6)" : "rgba(0,0,0,.45)",
      backdropFilter: "blur(4px)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      opacity: this.state.mounted ? 1 : 0,
      transition: "opacity 140ms ease-out",
      zIndex: 9999,
    };

    // Slightly taller dialog + a bit more padding
    const dialogStyle: React.CSSProperties = {
      width: "92vw",
      maxWidth: 520,
      minHeight: 220, // 👈 a little taller
      borderRadius: 16,
      background: isDark ? "#141414" : "#ffffff",
      color: isDark ? "#f5f5f5" : "#111111",
      border: `1px solid rgba(199,150,86,.35)`,
      boxShadow: "0 12px 44px rgba(0,0,0,.35)",
      transform: this.state.mounted ? "translateY(0) scale(1)" : "translateY(6px) scale(.98)",
      transition: "transform 180ms ease-out",
      padding: "24px 24px 22px", // slightly more breathing room
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
    };

    const iconWrapStyle: React.CSSProperties = {
      width: 48,
      height: 48,
      margin: "4px auto 12px",
      opacity: 0.95,
      flexShrink: 0,
    };

    const titleStyle: React.CSSProperties = {
      margin: "0 0 8px",
      fontSize: "1.15rem",
      textAlign: "center",
      lineHeight: 1.2,
      flexShrink: 0,
    };

    const descStyle: React.CSSProperties = {
      margin: 0,
      textAlign: "center",
      lineHeight: 1.55,
      opacity: 0.9,
      // let the text take available vertical space naturally
    };

    // A touch more gap above actions
    const actionsStyle: React.CSSProperties = {
      display: "flex",
      gap: 12,
      marginTop: 24, // 👈 was 18
      flexShrink: 0,
    };

    const btnBase: React.CSSProperties = {
      border: "0",
      borderRadius: 12,
      padding: "12px 16px",
      fontWeight: 600,
      cursor: "pointer",
      outline: "none",
      transition: "transform 60ms ease, box-shadow 60ms ease, filter 60ms ease, background 60ms ease",
      flex: 1,
    };

    // Subtle 2px focus ring
    const ring = (focused: boolean) => (focused ? "0 0 0 2px rgba(199,150,86,.28)" : "none");

    const primaryStyle = (
      hover: boolean,
      active: boolean,
      focused: boolean
    ): React.CSSProperties => ({
      ...btnBase,
      background: accent,
      color: "#ffffff",
      filter: hover ? "brightness(.97)" : "none",
      transform: hover && !active ? "translateY(-1px)" : "translateY(0)",
      boxShadow: ring(focused),
    });

    const secondaryStyle = (
      hover: boolean,
      active: boolean,
      focused: boolean
    ): React.CSSProperties => ({
      ...btnBase,
      background: hover ? "rgba(199,150,86,.08)" : "transparent",
      color: isDark ? "#f5f5f5" : "#111111",
      border: "1px solid rgba(199,150,86,.45)",
      transform: hover && !active ? "translateY(-1px)" : "translateY(0)",
      boxShadow: ring(focused),
    });

    const dialog = (
      <div style={overlayStyle} onClick={this.handleOverlayClick} role="presentation">
        <div
          style={dialogStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ays-title"
          aria-describedby="ays-desc"
          onClick={this.stop}
        >
          <div style={iconWrapStyle} aria-hidden="true">
            <svg viewBox="0 0 48 48" width="48" height="48" focusable="false" aria-hidden="true">
              <circle cx="24" cy="24" r="22" fill="none" stroke={accent} strokeWidth="2.5" />
              <rect x="22.5" y="12" width="3" height="18" rx="1.5" fill={accent} />
              <circle cx="24" cy="36" r="2" fill={accent} />
            </svg>
          </div>

          <h2 id="ays-title" style={titleStyle}>Are you sure?</h2>
          <p id="ays-desc" style={descStyle}>{this.props.message}</p>

          <div style={actionsStyle}>
            <button
              type="button"
              ref={this.cancelRef}
              style={secondaryStyle(this.state.hoverCancel, this.state.activeCancel, this.state.focusCancel)}
              onMouseEnter={() => this.setState({ hoverCancel: true })}
              onMouseLeave={() => this.setState({ hoverCancel: false, activeCancel: false })}
              onMouseDown={() => this.setState({ activeCancel: true })}
              onMouseUp={() => this.setState({ activeCancel: false })}
              onFocus={() => this.setState({ focusCancel: true })}
              onBlur={() => this.setState({ focusCancel: false })}
              onClick={() => this.props.onClose(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              ref={this.continueRef}
              style={primaryStyle(this.state.hoverContinue, this.state.activeContinue, this.state.focusContinue)}
              onMouseEnter={() => this.setState({ hoverContinue: true })}
              onMouseLeave={() => this.setState({ hoverContinue: false, activeContinue: false })}
              onMouseDown={() => this.setState({ activeContinue: true })}
              onMouseUp={() => this.setState({ activeContinue: false })}
              onFocus={() => this.setState({ focusContinue: true })}
              onBlur={() => this.setState({ focusContinue: false })}
              onClick={() => this.props.onClose(true)}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );

    return ReactDOM.createPortal(dialog, document.body);
  }
}
